import {
  Badge,
  Button,
  type ButtonProps,
  Callout,
  CheckboxField,
  FileCard,
  Upload,
} from '@dev-spendesk/grapes';
import { Trans } from 'react-i18next';

import { useTranslation } from 'common/hooks/useTranslation';
import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { apiUrl } from 'src/core/utils/api';

type Props = {
  templateButtonVariant?: ButtonProps['variant'];
  file: File | undefined;
  hasMissingFileError: boolean;
  prefixNameWithCode: boolean;
  setFile: (file: File | undefined) => void;
  setHasMissingFileError: (hasError: boolean) => void;
  setPrefixNameWithCode: (prefixNameWithCode: boolean) => void;
};

export const BulkImportExpenseAccountForm = ({
  templateButtonVariant = 'secondaryNeutral',
  file,
  hasMissingFileError,
  prefixNameWithCode,
  setFile,
  setHasMissingFileError,
  setPrefixNameWithCode,
}: Props) => {
  const { t } = useTranslation('global');

  const companyId = useCompanyId();
  const templateLink = apiUrl(
    `/accounting-integration/chart-of-accounts/expense-accounts/export-template`,
    companyId,
  );

  return (
    <div>
      <div className="mb-16 flex gap-16 text-left text-content-primary title-m">
        <Badge variant="primary">1</Badge>
        {t(
          'bookkeep.integrations.settings.expenseAccountsTable.import.instructionsDownload',
        )}
      </div>
      <Button
        className="ml-32"
        component="a"
        href={templateLink}
        iconName="arrow-down-tray"
        text={t(
          'bookkeep.integrations.settings.expenseAccountsTable.import.downloadButton',
        )}
        variant={templateButtonVariant}
      />
      <div className="mb-12 mt-24 flex gap-16 text-left text-content-primary title-m">
        <Badge variant="primary">2</Badge>
        {t(
          'bookkeep.integrations.settings.expenseAccountsTable.import.instructionsUpload',
        )}
      </div>
      <div>
        {!file ? (
          <>
            <Upload
              activeDragContent={
                <>{t('forms.uploadFiles.activeDragContent')}</>
              }
              content={
                <Trans i18nKey="forms.uploadFiles.content">
                  <br />-
                  <span className="text-content-primary underline title-m">-</span>
                </Trans>
              }
              onUpload={(files) => {
                setHasMissingFileError(false);
                setFile(files[0]);
              }}
            />
            {hasMissingFileError && (
              <Callout
                className="mt-8"
                title={t(
                  'bookkeep.integrations.settings.expenseAccountsTable.import.errorMissingFile',
                )}
                variant="alert"
              />
            )}
          </>
        ) : (
          <FileCard title={file.name} onDelete={() => setFile(undefined)} />
        )}
      </div>
      <CheckboxField
        className="mt-16 w-full text-left"
        isChecked={prefixNameWithCode}
        label={t(
          'bookkeep.integrations.settings.expenseAccountsTable.import.prefixWithCode',
        )}
        onChange={(event) => setPrefixNameWithCode(event.target.checked)}
      />
    </div>
  );
};
